import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/ui/date-picker';
import { toast } from 'sonner';
import {
  Car,
  Shield,
  Mail,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  User,
  Phone,
  MapPin,
  CheckCircle,
  Star,
  Upload,
  FileText,
  Calendar,
  Wrench,
  Camera,
  Clock,
  DollarSign
} from 'lucide-react';
import { formatDateToLocalString } from '@/lib/utils';

const RegisterDriverPage = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    dateOfBirth: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',

    // Driver Information
    licenseNumber: '',
    licenseExpiry: '',
    licenseIssue: '',
    licenseIssuingState: '',

    // Vehicle Information
    vehicleType: '',
    vehicleMake: '',
    vehicleModel: '',
    vehicleYear: '',
    vehicleColor: '',
    vehiclePlate: '',
    vehicleVin: '',
    insuranceNumber: '',
    insuranceExpiry: '',

    // Banking Information
    bankName: '',
    accountNumber: '',
    routingNumber: '',

    // Preferences
    preferredServiceAreas: [] as string[],
    workSchedule: '',
    availability: '',

    // Security
    password: '',
    confirmPassword: '',
    acceptTerms: false,
    acceptBackgroundCheck: false,
    acceptMarketing: false
  });

  const totalSteps = 4;
  const progress = (currentStep / totalSteps) * 100;

  const vehicleTypes = [
    { value: 'sedan', label: 'Sedan', capacity: '4 passengers', earnings: '$$$' },
    { value: 'suv', label: 'SUV', capacity: '6 passengers', earnings: '$$$$' },
    { value: 'van', label: 'Van', capacity: '8 passengers', earnings: '$$$$' },
    { value: 'motorcycle', label: 'Motorcycle', capacity: '1 passenger', earnings: '$$' },
    { value: 'truck', label: 'Truck', capacity: 'Cargo only', earnings: '$$$$' }
  ];

  const workSchedules = [
    'Full-time (40+ hours/week)',
    'Part-time (20-39 hours/week)',
    'Weekends only',
    'Evenings only',
    'Flexible/On-demand'
  ];

  const serviceAreas = [
    'Downtown',
    'Airport District',
    'University Area',
    'Business District',
    'Suburban Areas',
    'All Areas'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleServiceAreaToggle = (area) => {
    setFormData(prev => ({
      ...prev,
      preferredServiceAreas: prev.preferredServiceAreas.includes(area)
        ? prev.preferredServiceAreas.filter(a => a !== area)
        : [...prev.preferredServiceAreas, area]
    }));
  };

  const validateStep1 = () => {
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return false;
    }
    if (!formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!formData.dateOfBirth) {
      toast.error('Please enter your date of birth');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.licenseNumber || !formData.licenseExpiry || !formData.licenseIssue) {
      toast.error('Please fill in all license information');
      return false;
    }
    if (!formData.vehicleType || !formData.vehicleMake || !formData.vehicleModel || !formData.vehicleYear) {
      toast.error('Please fill in all vehicle information');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.vehiclePlate || !formData.insuranceNumber || !formData.insuranceExpiry) {
      toast.error('Please fill in all vehicle documentation');
      return false;
    }
    if (!formData.bankName || !formData.accountNumber || !formData.routingNumber) {
      toast.error('Please fill in all banking information');
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    if (!formData.password || !formData.confirmPassword) {
      toast.error('Please fill in password fields');
      return false;
    }
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    if (!formData.acceptTerms) {
      toast.error('Please accept the Terms of Service');
      return false;
    }
    if (!formData.acceptBackgroundCheck) {
      toast.error('Please consent to background check');
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    } else if (currentStep === 2 && validateStep2()) {
      setCurrentStep(3);
    } else if (currentStep === 3 && validateStep3()) {
      setCurrentStep(4);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    if (validateStep4()) {
      toast.success('Driver registration successful! Your application is being reviewed...');

      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  };

  const driverBenefits = [
    { icon: <DollarSign className="w-5 h-5" />, title: "Competitive Earnings", desc: "Earn up to $1500+ weekly with our dynamic pricing" },
    { icon: <Clock className="w-5 h-5" />, title: "Flexible Schedule", desc: "Choose when and where you want to drive" },
    { icon: <Shield className="w-5 h-5" />, title: "Insurance Coverage", desc: "Comprehensive insurance while driving" },
    { icon: <Star className="w-5 h-5" />, title: "Driver Rewards", desc: "Bonus programs and loyalty rewards" }
  ];

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
      <div className="w-full max-w-6xl">
        {/* Back to home */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to home
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Left side - Benefits */}
          <div className="space-y-6 lg:sticky lg:top-8">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-primary rounded-xl flex items-center justify-center">
                  <Car className="w-7 h-7 text-primary-foreground" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-foreground">Drive with Aboosto</h1>
                  <div className="flex items-center space-x-2">
                    <Badge className="bg-success/10 text-success border-success/20">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Instant Payouts
                    </Badge>
                    <Badge className="bg-primary/10 text-primary border-primary/20">
                      <Star className="w-3 h-3 mr-1" />
                      Top Rated
                    </Badge>
                  </div>
                </div>
              </div>

              <p className="text-lg text-muted-foreground">
                Join thousands of drivers earning more with flexible schedules and competitive rates.
              </p>
            </div>

            <div className="space-y-4">
              {driverBenefits.map((benefit, index) => (
                <div key={index} className="flex items-start space-x-4 p-4 rounded-lg bg-card border border-border/50 hover-lift">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center text-primary">
                    {benefit.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground">{benefit.title}</h3>
                    <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-primary/10 border border-primary/20 rounded-lg p-6">
              <div className="flex items-center space-x-2 text-primary mb-3">
                <DollarSign className="w-6 h-6" />
                <span className="font-semibold text-lg">Driver Requirements</span>
              </div>
              <ul className="space-y-2 text-sm text-primary/80">
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Valid driver's license (21+ years old)</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Smartphone (iOS 12+ or Android 8+)</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Vehicle inspection and insurance</span>
                </li>
                <li className="flex items-center space-x-2">
                  <CheckCircle className="w-4 h-4" />
                  <span>Background check consent</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right side - Form */}
          <Card className="border-border/50 shadow-elegant">
            <CardHeader className="space-y-4 pb-6">
              <div className="space-y-2">
                <h2 className="text-xl font-semibold text-foreground">Driver Registration</h2>
                <p className="text-sm text-muted-foreground">Step {currentStep} of {totalSteps}</p>
              </div>

              <Progress value={progress} className="h-2" />
            </CardHeader>

            <CardContent className="space-y-6">
              <form onSubmit={handleRegister} className="space-y-6">
                {/* Step 1: Personal Information */}
                {currentStep === 1 && (
                  <div className="space-y-4">
                    <h3 className="text-lg font-medium text-foreground">Personal Information</h3>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="firstName">First Name *</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input
                            id="firstName"
                            type="text"
                            placeholder="John"
                            className="pl-10"
                            value={formData.firstName}
                            onChange={(e) => handleInputChange('firstName', e.target.value)}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          type="text"
                          placeholder="Smith"
                          value={formData.lastName}
                          onChange={(e) => handleInputChange('lastName', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="email"
                          type="email"
                          placeholder="john.smith@email.com"
                          className="pl-10"
                          value={formData.email}
                          onChange={(e) => handleInputChange('email', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="phone"
                          type="tel"
                          placeholder="+1 (555) 123-4567"
                          className="pl-10"
                          value={formData.phone}
                          onChange={(e) => handleInputChange('phone', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="dateOfBirth">Date of Birth *</Label>
                      <DatePicker
                        value={formData.dateOfBirth ? new Date(formData.dateOfBirth) : undefined}
                        onChange={(date) => handleInputChange('dateOfBirth', date ? formatDateToLocalString(date) : '')}
                        placeholder="Select date of birth"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="address">Home Address *</Label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="address"
                          type="text"
                          placeholder="123 Main Street"
                          className="pl-10"
                          value={formData.address}
                          onChange={(e) => handleInputChange('address', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="city">City *</Label>
                        <Input
                          id="city"
                          type="text"
                          placeholder="New York"
                          value={formData.city}
                          onChange={(e) => handleInputChange('city', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="state">State *</Label>
                        <Input
                          id="state"
                          type="text"
                          placeholder="NY"
                          value={formData.state}
                          onChange={(e) => handleInputChange('state', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="zipCode">ZIP Code *</Label>
                        <Input
                          id="zipCode"
                          type="text"
                          placeholder="10001"
                          value={formData.zipCode}
                          onChange={(e) => handleInputChange('zipCode', e.target.value)}
                        />
                      </div>
                    </div>

                    <Button
                      type="button"
                      onClick={handleNext}
                      className="w-full bg-gradient-primary hover:opacity-90"
                    >
                      Continue to License Info
                      <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                    </Button>
                  </div>
                )}

                {/* Step 2: License & Vehicle */}
                {currentStep === 2 && (
                  <div className="space-y-4">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setCurrentStep(1)}
                      className="mb-4"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>

                    <h3 className="text-lg font-medium text-foreground">Driver's License</h3>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="licenseNumber">License Number *</Label>
                        <Input
                          id="licenseNumber"
                          type="text"
                          placeholder="D1234567"
                          value={formData.licenseNumber}
                          onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="licenseIssuingState">Issuing State *</Label>
                        <Input
                          id="licenseIssuingState"
                          type="text"
                          placeholder="NY"
                          value={formData.licenseIssuingState}
                          onChange={(e) => handleInputChange('licenseIssuingState', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="licenseIssue">Issue Date *</Label>
                        <DatePicker
                          value={formData.licenseIssue ? new Date(formData.licenseIssue) : undefined}
                          onChange={(date) => handleInputChange('licenseIssue', date ? formatDateToLocalString(date) : '')}
                          placeholder="Select license issue date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="licenseExpiry">Expiry Date *</Label>
                        <DatePicker
                          value={formData.licenseExpiry ? new Date(formData.licenseExpiry) : undefined}
                          onChange={(date) => handleInputChange('licenseExpiry', date ? formatDateToLocalString(date) : '')}
                          placeholder="Select license expiry date"
                        />
                      </div>
                    </div>

                    <h3 className="text-lg font-medium text-foreground pt-4">Vehicle Information</h3>

                    <div className="space-y-2">
                      <Label>Vehicle Type *</Label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {vehicleTypes.map((type) => (
                          <div key={type.value} className="border rounded-lg p-3 cursor-pointer hover:bg-primary/5 transition-colors">
                            <div className="flex items-start space-x-3">
                              <Checkbox
                                checked={formData.vehicleType === type.value}
                                onCheckedChange={(checked) => checked && handleInputChange('vehicleType', type.value)}
                                className="mt-1"
                              />
                              <div className="flex-1">
                                <div className="font-medium">{type.label}</div>
                                    <div className="text-sm text-muted-foreground">{type.capacity}</div>
                                    <div className="text-xs text-success font-medium">{type.earnings} potential</div>
                                  </div>
                                </div>
                              </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="vehicleMake">Make *</Label>
                        <Input
                          id="vehicleMake"
                          type="text"
                          placeholder="Toyota"
                          value={formData.vehicleMake}
                          onChange={(e) => handleInputChange('vehicleMake', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vehicleModel">Model *</Label>
                        <Input
                          id="vehicleModel"
                          type="text"
                          placeholder="Camry"
                          value={formData.vehicleModel}
                          onChange={(e) => handleInputChange('vehicleModel', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="vehicleYear">Year *</Label>
                        <Input
                          id="vehicleYear"
                          type="text"
                          placeholder="2020"
                          value={formData.vehicleYear}
                          onChange={(e) => handleInputChange('vehicleYear', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vehicleColor">Color *</Label>
                        <Input
                          id="vehicleColor"
                          type="text"
                          placeholder="Black"
                          value={formData.vehicleColor}
                          onChange={(e) => handleInputChange('vehicleColor', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="vehiclePlate">Plate Number *</Label>
                        <Input
                          id="vehiclePlate"
                          type="text"
                          placeholder="ABC123"
                          value={formData.vehiclePlate}
                          onChange={(e) => handleInputChange('vehiclePlate', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="vehicleVin">VIN (Optional)</Label>
                      <Input
                        id="vehicleVin"
                        type="text"
                        placeholder="1HGCM82633A123456"
                        value={formData.vehicleVin}
                        onChange={(e) => handleInputChange('vehicleVin', e.target.value)}
                      />
                    </div>

                    <Button
                      type="button"
                      onClick={handleNext}
                      className="w-full bg-gradient-primary hover:opacity-90"
                    >
                      Continue to Documentation
                      <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                    </Button>
                  </div>
                )}

                {/* Step 3: Documentation & Banking */}
                {currentStep === 3 && (
                  <div className="space-y-4">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setCurrentStep(2)}
                      className="mb-4"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>

                    <h3 className="text-lg font-medium text-foreground">Insurance Information</h3>

                    <div className="space-y-2">
                      <Label htmlFor="insuranceNumber">Policy Number *</Label>
                      <div className="relative">
                        <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="insuranceNumber"
                          type="text"
                          placeholder="POL123456789"
                          className="pl-10"
                          value={formData.insuranceNumber}
                          onChange={(e) => handleInputChange('insuranceNumber', e.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="insuranceExpiry">Insurance Expiry *</Label>
                      <DatePicker
                        value={formData.insuranceExpiry ? new Date(formData.insuranceExpiry) : undefined}
                        onChange={(date) => handleInputChange('insuranceExpiry', date ? formatDateToLocalString(date) : '')}
                        placeholder="Select insurance expiry date"
                      />
                    </div>

                    <h3 className="text-lg font-medium text-foreground pt-4">Banking Information</h3>

                    <div className="space-y-2">
                      <Label htmlFor="bankName">Bank Name *</Label>
                      <Input
                        id="bankName"
                        type="text"
                        placeholder="Chase Bank"
                        value={formData.bankName}
                        onChange={(e) => handleInputChange('bankName', e.target.value)}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="accountNumber">Account Number *</Label>
                        <Input
                          id="accountNumber"
                          type="text"
                          placeholder="123456789"
                          value={formData.accountNumber}
                          onChange={(e) => handleInputChange('accountNumber', e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="routingNumber">Routing Number *</Label>
                        <Input
                          id="routingNumber"
                          type="text"
                          placeholder="021000021"
                          value={formData.routingNumber}
                          onChange={(e) => handleInputChange('routingNumber', e.target.value)}
                        />
                      </div>
                    </div>

                    <h3 className="text-lg font-medium text-foreground pt-4">Service Preferences</h3>

                    <div className="space-y-2">
                      <Label>Preferred Service Areas</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {serviceAreas.map((area) => (
                          <div key={area} className="flex items-center space-x-2">
                            <Checkbox
                              id={area}
                              checked={formData.preferredServiceAreas.includes(area)}
                              onCheckedChange={() => handleServiceAreaToggle(area)}
                            />
                            <Label htmlFor={area} className="text-sm">{area}</Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="workSchedule">Work Schedule *</Label>
                      <Select value={formData.workSchedule} onValueChange={(value) => handleInputChange('workSchedule', value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select work schedule" />
                        </SelectTrigger>
                        <SelectContent>
                          {workSchedules.map((schedule) => (
                            <SelectItem key={schedule} value={schedule}>
                              {schedule}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="availability">Additional Availability Info</Label>
                      <Textarea
                        id="availability"
                        placeholder="Any specific hours or days you prefer to work..."
                        className="min-h-20"
                        value={formData.availability}
                        onChange={(e) => handleInputChange('availability', e.target.value)}
                      />
                    </div>

                    <Button
                      type="button"
                      onClick={handleNext}
                      className="w-full bg-gradient-primary hover:opacity-90"
                    >
                      Continue to Security
                      <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                    </Button>
                  </div>
                )}

                {/* Step 4: Security & Terms */}
                {currentStep === 4 && (
                  <div className="space-y-4">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={() => setCurrentStep(3)}
                      className="mb-4"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back
                    </Button>

                    <h3 className="text-lg font-medium text-foreground">Account Security</h3>

                    <div className="space-y-2">
                      <Label htmlFor="password">Password *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Create a strong password (min 8 chars)"
                          className="pl-10 pr-10"
                          value={formData.password}
                          onChange={(e) => handleInputChange('password', e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword">Confirm Password *</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="confirmPassword"
                          type={showConfirmPassword ? 'text' : 'password'}
                          placeholder="Confirm your password"
                          className="pl-10 pr-10"
                          value={formData.confirmPassword}
                          onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="terms"
                          checked={formData.acceptTerms}
                          onCheckedChange={(checked) => handleInputChange('acceptTerms', checked)}
                          className="mt-1"
                        />
                        <Label htmlFor="terms" className="text-sm leading-5">
                          I agree to the <Link to="/terms" className="text-primary hover:text-primary/80">Driver Terms of Service</Link> and <Link to="/privacy" className="text-primary hover:text-primary/80">Privacy Policy</Link>
                        </Label>
                      </div>

                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="background"
                          checked={formData.acceptBackgroundCheck}
                          onCheckedChange={(checked) => handleInputChange('acceptBackgroundCheck', checked)}
                          className="mt-1"
                        />
                        <Label htmlFor="background" className="text-sm leading-5">
                          I consent to a background check and driving record verification
                        </Label>
                      </div>

                      <div className="flex items-start space-x-2">
                        <Checkbox
                          id="marketing"
                          checked={formData.acceptMarketing}
                          onCheckedChange={(checked) => handleInputChange('acceptMarketing', checked)}
                          className="mt-1"
                        />
                        <Label htmlFor="marketing" className="text-sm leading-5">
                          Send me driving tips, promotions, and platform updates
                        </Label>
                      </div>
                    </div>

                    <Button type="submit" className="w-full bg-gradient-primary hover:opacity-90 h-12">
                      <Car className="w-5 h-5 mr-2" />
                      Complete Driver Registration
                    </Button>
                  </div>
                )}
              </form>

              <div className="text-center text-sm">
                <span className="text-muted-foreground">Already have an account? </span>
                <Link to="/login" className="text-primary hover:text-primary/80 font-medium transition-colors">
                  Sign in
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterDriverPage;